import React, { useState, useEffect } from 'react';
import { Card, Modal, Button } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { fetchParticipationPerformance, evaluateAnswer } from 'ServerAPI/serverAPI';
import { Shield, Monitor, Eye, Copy, AlertTriangle, Clock } from 'react-feather';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);
import Questions from '../course-resume/Quiz/Questions';

// Normalizes whatever attempt shape the backend sends into a common list, newest last:
// - maxAttemptsHistory (when present & non-empty): full per-attempt quizResults, every attempt
//   reviewable. This is the Max-Attempts-specific field — distinct from the general, pre-existing
//   attemptCount/attempts fields used elsewhere for unrelated attempt reporting (e.g. FA monitoring).
// - Old flow (no maxAttemptsHistory): `quizResults` is always the authoritative latest-attempt
//   detail — this is a valid, expected shape (single-attempt records, or any record saved before
//   Max Attempts existed), not malformed data, so it must win over the detail-free legacy
//   `attempts` array below.
// - `attempts` (legacy, { score, date } only — no per-question detail): last resort, only used when
//   neither of the above is available.
const buildAttemptsList = (userAnswers) => {
    const unwrap = (v) => v?.$date || v || null;

    if (Array.isArray(userAnswers?.maxAttemptsHistory) && userAnswers.maxAttemptsHistory.length > 0) {
        return userAnswers.maxAttemptsHistory
            .map((a) => ({
                attemptNumber: a.attemptNumber,
                submittedAt: unwrap(a.submittedAt),
                passed: typeof a.passed === 'boolean' ? a.passed : (typeof a.quizResults?.passed === 'boolean' ? a.quizResults.passed : null),
                quizResults: a.quizResults || {}
            }))
            .sort((a, b) => a.attemptNumber - b.attemptNumber);
    }

    if (userAnswers?.quizResults) {
        return [{
            attemptNumber: userAnswers?.maxAttemptsUsed || 1,
            submittedAt: unwrap(userAnswers.quizResults.quizSubmittedAt) || unwrap(userAnswers.quizSubmittedAt),
            passed: typeof userAnswers.quizResults.passed === 'boolean' ? userAnswers.quizResults.passed : null,
            quizResults: userAnswers.quizResults
        }];
    }

    if (Array.isArray(userAnswers?.attempts) && userAnswers.attempts.length > 0) {
        return [...userAnswers.attempts]
            .sort((a, b) => new Date(unwrap(a.date) || 0) - new Date(unwrap(b.date) || 0))
            .map((a, idx) => ({
                attemptNumber: idx + 1,
                submittedAt: unwrap(a.date),
                passed: null,
                quizResults: { results: a.results || [], score: a.score }
            }));
    }

    return [];
};

const QuizReviewDisplay = ({ show, handleClose, courseId, contentId, userId, quizData }) => {
    const [questions, setQuestions] = useState([]);
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [correctAnswers, setCorrectAnswers] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sectionId, setSectionId] = useState(null);
    const [quizTimestamps, setQuizTimestamps] = useState(null);
    const [quizConfig, setQuizConfig] = useState(null);
    const [rawUserAnswers, setRawUserAnswers] = useState(null);
    const [overriddenMarks, setOverriddenMarks] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [proctoringLogs, setProctoringLogs] = useState(null);
    const [questionTimeMap, setQuestionTimeMap] = useState({});
    const [attemptsList, setAttemptsList] = useState([]);
    const [selectedAttemptNumber, setSelectedAttemptNumber] = useState(null);

    useEffect(() => {
        if (!show) {
            setOverriddenMarks({});
            setShowConfirmModal(false);
            setProctoringLogs(null);
            setQuestionTimeMap({});
        }
    }, [show]);

    useEffect(() => {
        const fetchQuizData = async () => {
            if (quizData) {
                await processQuizData(quizData);
            } else if (courseId && contentId && userId) {
                setLoading(true);
                try {
                    const payload = {
                        courseId,
                        contentId,
                        userId,
                        type: 'quiz'
                    };
                    const response = await fetchParticipationPerformance(payload);
                    console.log('fetchParticipationPerformance response:', response);

                    if (response?.status === 200) {
                        await processQuizData(response.data);
                    } else {
                        toast.error('Failed to fetch quiz data: ' + (response?.message || 'No quiz found for contentId: ' + contentId));
                    }
                } catch (error) {
                    console.error('Error fetching quiz data:', error);
                    toast.error('Error fetching quiz data: ' + error.message);
                } finally {
                    setLoading(false);
                }
            }
        };

        const processQuizData = async (data) => {
            const quiz = data.quiz;
            const userAnswers = data.userAnswers;

            // Reset per-quiz display state up front, before the early-return below — otherwise
            // switching from a proctored quiz to one with no questions (or any other malformed
            // record) leaves stale state (e.g. quizConfig.isProctored) from the previous quiz.
            setQuizConfig(null);
            setQuestionTimeMap({});
            setProctoringLogs(null);

            if (!quiz?.questions?.length) {
                console.warn('No questions found in quiz data:', data);
                toast.warn('No quiz questions available');
                setQuestions([]);
                return;
            }

            const sectionIdData = quiz.sectionId || data.sectionId;
            const questionsData = quiz.questions;

            setQuestions(questionsData);
            setSectionId(sectionIdData);
            setRawUserAnswers(userAnswers);

            const list = buildAttemptsList(userAnswers);
            setAttemptsList(list);
            setSelectedAttemptNumber(list.length > 0 ? list[list.length - 1].attemptNumber : null);

            if (quiz) {
                setQuizConfig({
                    startDate: quiz.startDate,
                    startTime: quiz.startTime,
                    endDate: quiz.endDate,
                    endTime: quiz.endTime,
                    quizTimer: quiz.quizTimer,
                    passingPercentage: quiz.passingPercentage,
                    scheduledAttempt: quiz.scheduledAttempt,
                    finalAssessment: quiz.finalAssessment,
                    isProctored: quiz.isProctored
                });
            } else {
                setQuizConfig(null);
            }
        };

        fetchQuizData();
    }, [quizData, courseId, contentId, userId]);

    // Projects whichever attempt is selected into the review UI. Historical attempts
    // (maxAttemptsHistory) only carry a `submittedAt` timestamp, not a start time — only the
    // latest attempt (top-level userAnswers) has both, so duration only shows for that one.
    useEffect(() => {
        if (!attemptsList.length || selectedAttemptNumber === null) {
            setSelectedAnswers({});
            setCorrectAnswers([]);
            setResults([]);
            setProctoringLogs(null);
            setQuestionTimeMap({});
            setQuizTimestamps(null);
            return;
        }

        const latestAttemptNumber = attemptsList[attemptsList.length - 1].attemptNumber;
        const attemptEntry = attemptsList.find((a) => a.attemptNumber === selectedAttemptNumber) || attemptsList[attemptsList.length - 1];
        const isLatest = attemptEntry.attemptNumber === latestAttemptNumber;
        const attemptQuizResults = attemptEntry.quizResults || {};

        const correctAnswersData = attemptQuizResults?.results?.map(result => ({
            questionId: result.questionId,
            correctValue: result.correctValue
        })) || [];

        const resultsData = attemptQuizResults?.results || [];
        const selectedAnswersData = {};
        resultsData.forEach(result => {
            selectedAnswersData[result.questionId] = result.userAnswer;
        });

        setSelectedAnswers(selectedAnswersData);
        setCorrectAnswers(correctAnswersData);
        setResults(resultsData);
        setProctoringLogs(attemptQuizResults?.proctoringLogs || null);
        setOverriddenMarks({}); // marks overrides are attempt-specific; don't carry over on switch

        const timeTrackingData = attemptQuizResults?.questionTimeTracking || [];
        const timeMap = timeTrackingData.reduce((acc, t) => {
            acc[String(t.questionId)] = t.timeSpentSeconds;
            return acc;
        }, {});
        setQuestionTimeMap(timeMap);

        const startedAt = isLatest ? (rawUserAnswers?.quizResults?.quizStartedAt || rawUserAnswers?.quizStartedAt) : null;
        const submittedAt = attemptEntry.submittedAt || (isLatest ? rawUserAnswers?.quizResults?.quizSubmittedAt : null);
        if (startedAt && submittedAt) {
            setQuizTimestamps({
                start: dayjs(startedAt).format('MMM D, YYYY h:mm A'),
                end: dayjs(submittedAt).format('MMM D, YYYY h:mm A'),
                duration: dayjs.duration(dayjs(submittedAt).diff(dayjs(startedAt))).format('m [mins] s [secs]'),
            });
        } else if (submittedAt) {
            setQuizTimestamps({ start: null, end: dayjs(submittedAt).format('MMM D, YYYY h:mm A'), duration: null });
        } else {
            setQuizTimestamps(null);
        }
    }, [selectedAttemptNumber, attemptsList, rawUserAnswers]);

    const isViewingLatestAttempt = attemptsList.length === 0 || selectedAttemptNumber === attemptsList[attemptsList.length - 1]?.attemptNumber;

    const handleMarksChange = (questionId, newMarks) => {
        if (newMarks === null) {
            setOverriddenMarks(prev => {
                const updated = { ...prev };
                delete updated[questionId];
                return updated;
            });
        } else {
            setOverriddenMarks(prev => ({ ...prev, [questionId]: newMarks }));
        }
    };

    const handleDoneClick = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmDone = async () => {
        setShowConfirmModal(false);
        setSubmitting(true);
        try {
            const safeQuestions = Array.isArray(questions) ? questions : [];
            const questionID_userAns = safeQuestions.map((question) => {
                const entry = {
                    questionId: String(question.id),
                    userAnswer: String(selectedAnswers[question.id] || ""),
                    answerSelectionType: question?.answerSelectionType || "single",
                    point: question?.point || 1,
                };
                if (overriddenMarks[question.id] !== undefined) {
                    entry.edited_marks = overriddenMarks[question.id];
                }
                return entry;
            });

            // Marks editing is only ever enabled while viewing the latest attempt (the Done
            // button is gated on isViewingLatestAttempt), so this is always the same attempt
            // being edited — send it explicitly rather than relying on the backend's implicit
            // "no attemptNumber = target the last attempt" default.
            const latestAttemptNumber = attemptsList[attemptsList.length - 1]?.attemptNumber;

            const payload = {
                questionID_userAns,
                courseId,
                sectionId,
                contentId,
                userId,
                status: "Completed",
                teacher_reviewed: true,
                ...(latestAttemptNumber !== undefined && { attemptNumber: latestAttemptNumber }),
            };

            console.log('Teacher review payload:', payload);
            const response = await evaluateAnswer(payload);
            if (response?.status === 200 || response?.data) {
                toast.success('Marks updated successfully!');
                handleClose();
            } else {
                toast.error('Failed to update marks. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting teacher review:', error);
            toast.error('Error submitting review: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;

    const safeQuestions = Array.isArray(questions) ? questions : [];
    const hasAnyEdits = Object.keys(overriddenMarks).length > 0;

    const formatQuestionTime = (seconds) => {
        if (seconds === undefined || seconds === null) return '—';
        if (seconds < 60) return `${Math.round(seconds)}s`;
        const m = Math.floor(seconds / 60);
        const s = Math.round(seconds % 60);
        return `${m}m ${s}s`;
    };
    const totalTrackedSeconds = Object.values(questionTimeMap).reduce((a, b) => a + b, 0);

    // Calculate passing logic
    const totalPotentialScore = safeQuestions.reduce((acc, q) => acc + (Number(q.point) || 1), 0);
    const originalTotalScore = safeQuestions.reduce((acc, q) => {
        const questionResult = Array.isArray(results)
            ? results.find((r) => String(r.questionId) === String(q.id) || String(r.questionId) === String(q.tempId))
            : undefined;
            
        const origScore = questionResult?.final_score !== undefined 
            ? Number(questionResult.final_score) 
            : (questionResult?.status === 'correct' ? (Number(q.point) || 1) : 0);
        return acc + origScore;
    }, 0);

    const newTotalScore = safeQuestions.reduce((acc, q) => {
        if (overriddenMarks[q.id] !== undefined) {
             return acc + Number(overriddenMarks[q.id]);
        }
        
        const questionResult = Array.isArray(results)
            ? results.find((r) => String(r.questionId) === String(q.id) || String(r.questionId) === String(q.tempId))
            : undefined;
            
        const origScore = questionResult?.final_score !== undefined 
            ? Number(questionResult.final_score) 
            : (questionResult?.status === 'correct' ? (Number(q.point) || 1) : 0);
        return acc + origScore;
    }, 0);

    const newPercentage = totalPotentialScore > 0 ? (newTotalScore / totalPotentialScore) * 100 : 0;
    const passingPercentage = quizConfig?.passingPercentage ? parseFloat(quizConfig.passingPercentage) : 0;
    const isFailingNow = passingPercentage > 0 && newPercentage < passingPercentage;

    return (
        <>
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 30000
            }}>
                <div style={{
                    background: 'white',
                    width: '90%',
                    maxWidth: '95vw',
                    maxHeight: '95vh',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                        background: 'rgba(34, 75, 153, 1)',
                        color: 'white',
                        flexShrink: 0
                    }}>
                        <h5 style={{ margin: 0, color: 'white' }}>
                            Review Quiz Attempt
                            {attemptsList.length > 0 && (
                                <span style={{
                                    marginLeft: '10px',
                                    fontSize: '0.75rem',
                                    fontWeight: 'normal',
                                    background: 'rgba(255,255,255,0.2)',
                                    padding: '3px 10px',
                                    borderRadius: '12px',
                                    verticalAlign: 'middle'
                                }}>
                                    {attemptsList.length} attempt{attemptsList.length === 1 ? '' : 's'}
                                </span>
                            )}
                        </h5>
                        <button
                            onClick={handleClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'white',
                                fontSize: '20px',
                                cursor: 'pointer'
                            }}
                        >
                            ✖
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
                        {loading ? (
                            <div style={{ textAlign: 'center' }}><CircularProgress /></div>
                        ) : safeQuestions.length === 0 ? (
                            <div style={{ textAlign: 'center' }}><CircularProgress /></div>
                        ) : results.length === 0 ? (
                            <Card style={{ marginBottom: '16px' }}>
                                <Card.Body>
                                    <h4>User has not passed/attempted the assessment.</h4>
                                </Card.Body>
                            </Card>
                        ) : (
                            <>
                                {attemptsList.length > 1 && (
                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                        {attemptsList.map((a) => {
                                            const isSelected = a.attemptNumber === selectedAttemptNumber;
                                            const isLatest = a.attemptNumber === attemptsList[attemptsList.length - 1].attemptNumber;
                                            return (
                                                <button
                                                    key={a.attemptNumber}
                                                    onClick={() => setSelectedAttemptNumber(a.attemptNumber)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        borderRadius: '20px',
                                                        border: isSelected ? '2px solid #224B99' : '1px solid #dee2e6',
                                                        background: isSelected ? '#224B99' : '#fff',
                                                        color: isSelected ? '#fff' : '#212529',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    Attempt {a.attemptNumber}
                                                    {a.passed !== null && (
                                                        <span style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            background: a.passed ? '#28a745' : '#dc3545',
                                                            display: 'inline-block'
                                                        }} />
                                                    )}
                                                    {isLatest && (
                                                        <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>(Latest)</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {quizConfig && (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                        padding: '16px',
                                        background: '#f0f4f8',
                                        borderRadius: '8px',
                                        marginBottom: '16px',
                                        border: '1px solid #d1d9e6',
                                        fontSize: '0.85rem'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', pb: '8px', marginBottom: '4px' }}>
                                            <span style={{ fontWeight: 'bold', color: '#224B99', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quiz Configuration (Manager View)</span>
                                            {quizConfig.finalAssessment && <span style={{ background: '#e8f0fe', color: '#1967d2', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '10px' }}>FINAL ASSESSMENT</span>}
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                                            <div>
                                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Scheduled Window</div>
                                                <div style={{ fontWeight: '500' }}>
                                                    {quizConfig.scheduledAttempt ? (
                                                        <>
                                                            <span style={{ color: '#28a745' }}>{quizConfig.startDate} {quizConfig.startTime}</span>
                                                            <span style={{ margin: '0 8px', color: '#adb5bd' }}>→</span>
                                                            <span style={{ color: '#dc3545' }}>{quizConfig.endDate} {quizConfig.endTime}</span>
                                                        </>
                                                    ) : <span style={{ color: '#6c757d' }}>Not Scheduled (Available anytime)</span>}
                                                </div>
                                            </div>
                                            <div style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '24px' }}>
                                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Timer</div>
                                                <div style={{ fontWeight: '500' }}>{quizConfig.quizTimer} Mins</div>
                                            </div>
                                            <div style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '24px' }}>
                                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Passing %</div>
                                                <div style={{ fontWeight: '500' }}>{quizConfig.passingPercentage}%</div>
                                            </div>
                                            <div style={{ borderLeft: '1px solid #dee2e6', paddingLeft: '24px', flex: 1, textAlign: 'right' }}>
                                                <div style={{ color: '#6c757d', marginBottom: '2px' }}>Student Score</div>
                                                <div style={{ 
                                                    fontSize: '1.2rem', 
                                                    fontWeight: 'bold', 
                                                    color: (totalPotentialScore > 0 && (originalTotalScore / totalPotentialScore * 100) >= quizConfig.passingPercentage) ? '#28a745' : '#dc3545' 
                                                }}>
                                                    {originalTotalScore.toFixed(1)} / {totalPotentialScore}
                                                    <span style={{ fontSize: '0.8rem', marginLeft: '8px', color: '#6c757d' }}>
                                                        ({totalPotentialScore > 0 ? (originalTotalScore / totalPotentialScore * 100).toFixed(1) : 0}%)
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {quizTimestamps && (
                                    <div style={{
                                        display: 'flex',
                                        gap: '20px',
                                        padding: '12px 16px',
                                        background: '#ffffff',
                                        borderRadius: '6px',
                                        marginBottom: '20px',
                                        border: '1px dotted #dee2e6',
                                        fontSize: '0.9rem'
                                    }}>
                                        {quizTimestamps.start && (
                                            <div>
                                                <span style={{ color: '#6c757d', fontWeight: '500' }}>Attempt Started:</span>
                                                <span style={{ marginLeft: '8px', color: '#212529' }}>{quizTimestamps.start}</span>
                                            </div>
                                        )}
                                        <div>
                                            <span style={{ color: '#6c757d', fontWeight: '500' }}>Attempt Submitted:</span>
                                            <span style={{ marginLeft: '8px', color: '#212529' }}>{quizTimestamps.end}</span>
                                        </div>
                                        {quizTimestamps.duration && (
                                            <div style={{ marginLeft: 'auto' }}>
                                                <span style={{ color: '#6c757d', fontWeight: '500' }}>Time Taken:</span>
                                                <span style={{ marginLeft: '8px', color: '#224B99', fontWeight: 'bold' }}>{quizTimestamps.duration}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {quizConfig?.isProctored && Object.keys(questionTimeMap).length > 0 && (
                                    <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', marginBottom: '16px', border: '1px solid #dee2e6', boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#224B99', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                <Clock size={16} />
                                                Time per Question
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                                Total tracked: <strong style={{ color: '#224B99' }}>{formatQuestionTime(totalTrackedSeconds)}</strong>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(safeQuestions.length || 1, 6)}, 1fr)`, gap: '10px' }}>
                                            {safeQuestions.map((q, idx) => {
                                                const t = questionTimeMap[String(q.id)];
                                                const visited = t !== undefined;
                                                const isFast = visited && t < 5;
                                                const bg = !visited ? '#f8f9fa' : isFast ? '#fffbf0' : '#f0fff4';
                                                const borderColor = !visited ? '#dee2e6' : isFast ? '#ffd97d' : '#c3e6cb';
                                                const textColor = !visited ? '#adb5bd' : isFast ? '#d97706' : '#224B99';
                                                return (
                                                    <div key={q.id} style={{ background: bg, border: `1px solid ${borderColor}`, borderRadius: '8px', padding: '10px 8px', textAlign: 'center' }}>
                                                        <div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '4px' }}>Q{idx + 1}</div>
                                                        <div style={{ fontSize: '1rem', fontWeight: '700', color: textColor }}>{visited ? formatQuestionTime(t) : '—'}</div>
                                                        <div style={{ fontSize: '0.62rem', fontWeight: '700', letterSpacing: '0.5px', marginTop: '2px', color: !visited ? '#adb5bd' : isFast ? '#d97706' : 'transparent' }}>
                                                            {!visited ? 'SKIPPED' : isFast ? 'FAST' : ' '}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {quizConfig?.isProctored && proctoringLogs && (() => {
                                    const totalViolations = proctoringLogs.reduce((sum, l) => sum + l.count, 0);
                                    const iconMap = { fullscreenExit: Monitor, tabSwitch: Eye, blockedShortcut: AlertTriangle, copyAttempt: Copy };
                                    return (
                                        <div style={{ padding: '16px', background: '#fff', borderRadius: '8px', marginBottom: '16px', border: '1px solid #dee2e6', boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#224B99', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    <Shield size={16} />
                                                    Proctoring Report
                                                </div>
                                                <div style={{
                                                    background: totalViolations === 0 ? '#d4edda' : '#fff3cd',
                                                    color: totalViolations === 0 ? '#155724' : '#856404',
                                                    padding: '4px 14px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600'
                                                }}>
                                                    {totalViolations === 0 ? '✓ No Violations' : `${totalViolations} Total Warning${totalViolations > 1 ? 's' : ''}`}
                                                </div>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                                                {proctoringLogs.map((log) => {
                                                    const Icon = iconMap[log.type] || Shield;
                                                    const isViolated = log.count > 0;
                                                    const isSevere = log.count >= 3;
                                                    const bg = isSevere ? '#fff5f5' : isViolated ? '#fffbf0' : '#f0fff4';
                                                    const borderColor = isSevere ? '#f5c6cb' : isViolated ? '#ffd97d' : '#c3e6cb';
                                                    const countColor = isSevere ? '#dc3545' : isViolated ? '#d97706' : '#28a745';
                                                    const iconColor = isSevere ? '#dc3545' : isViolated ? '#f39c12' : '#28a745';
                                                    const statusLabel = isViolated ? (isSevere ? 'HIGH RISK' : 'WARNING') : 'CLEAN';
                                                    return (
                                                        <div key={log.type} style={{ background: bg, border: `1px solid ${borderColor}`, borderRadius: '8px', padding: '14px 12px', textAlign: 'center' }}>
                                                            <div style={{ marginBottom: '6px', color: iconColor }}>
                                                                <Icon size={20} />
                                                            </div>
                                                            <div style={{ fontSize: '0.72rem', color: '#6c757d', marginBottom: '8px', lineHeight: '1.35', minHeight: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {log.label}
                                                            </div>
                                                            <div style={{ fontSize: '2rem', fontWeight: '800', color: countColor, lineHeight: '1' }}>
                                                                {log.count}
                                                            </div>
                                                            <div style={{ marginTop: '8px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.5px', color: countColor }}>
                                                                {statusLabel}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {!isViewingLatestAttempt && (
                                    <div style={{ padding: '10px 16px', background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: '6px', marginBottom: '16px', fontSize: '0.85rem' }}>
                                        Viewing a past attempt — read-only. Switch to the latest attempt to review or edit marks.
                                    </div>
                                )}

                                <Card style={{ marginBottom: '16px', border: '1px solid #dee2e6', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                                    <Card.Body>
                                        <Questions
                                          questions={safeQuestions}
                                          selectedAnswers={selectedAnswers}
                                          isReview={true}
                                          correctAnswers={correctAnswers}
                                          result={results}
                                          startIndex={1}
                                          courseId={courseId}
                                          sectionId={sectionId}
                                          userId={userId}
                                          isManagerReview={isViewingLatestAttempt}
                                          onMarksChange={isViewingLatestAttempt ? handleMarksChange : undefined}
                                          overriddenMarks={overriddenMarks}
                                          // Questions.js treats any truthy value here as "show the per-question
                                          // timer badge" — even an empty object `{}` is truthy in JS, so this
                                          // must only be passed at all for proctored quizzes, not just filtered
                                          // by content.
                                          questionTimeTracking={quizConfig?.isProctored ? questionTimeMap : undefined}
                                        />
                                    </Card.Body>
                                </Card>
                            </>
                        )}
                    </div>

                    {/* Footer with Done button — only actionable while reviewing the latest attempt */}
                    {!loading && results.length > 0 && isViewingLatestAttempt && (
                        <div style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #dee2e6',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            background: '#f8f9fa',
                            flexShrink: 0
                        }}>
                            <button
                                onClick={handleDoneClick}
                                disabled={submitting}
                                style={{
                                    background: '#224B99',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '10px 32px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    opacity: submitting ? 0.7 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                {submitting ? <><CircularProgress size={16} style={{ color: 'white' }} /> Submitting...</> : 'Done'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            <Modal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                centered
                style={{ zIndex: 40000 }}
                contentClassName="border border-2 border-primary shadow-lg rounded-3"
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1rem', fontWeight: '700', color: '#212529' }}>
                        Confirm Mark Submission
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ marginBottom: '8px' }}>
                        Are you sure you want to submit the reviewed marks?
                    </p>
                    <p style={{ color: '#dc3545', fontSize: '0.875rem', marginBottom: 0 }}>
                        ⚠️ <strong>This action cannot be undone.</strong> The marks for this quiz attempt will be updated and locked.
                    </p>
                    {hasAnyEdits && (
                        <div style={{ marginTop: '12px', padding: '10px 12px', background: '#f0f4f8', borderRadius: '6px', fontSize: '0.85rem' }}>
                            <strong>Edited questions:</strong>{' '}
                            {Object.entries(overriddenMarks).map(([qId, marks]) => {
                                const qIndex = safeQuestions.findIndex(q => String(q.id) === String(qId)) + 1;
                                return (
                                <span key={qId} style={{ marginRight: '8px', background: '#e8f0fe', color: '#224B99', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginBottom: '4px' }}>
                                    Q{qIndex}: {marks} marks
                                </span>
                                );
                            })}
                        </div>
                    )}
                    {isFailingNow && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#fff3cd', color: '#856404', borderRadius: '6px', fontSize: '0.875rem', border: '1px solid #ffeeba' }}>
                            ⚠️ <strong>Warning:</strong> The newly edited score (<strong>{newPercentage % 1 === 0 ? newPercentage : newPercentage.toFixed(1)}%</strong>) is below the passing requirement of <strong>{passingPercentage}%</strong>. <br/>If you submit these marks, the student will fail the quiz and will have to redo it.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDone}
                        style={{ background: '#224B99', border: 'none', fontWeight: '600' }}
                    >
                        Yes, Submit
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default QuizReviewDisplay;
